/**
 * The four inks a code sample is drawn in, on the `--bd-ink` slab.
 *
 * ## Why these are not `--bd-*` tokens
 *
 * The bedrock scale answers "what can prose be drawn in on a Syncanix
 * surface". A syntax palette answers a different question — it needs four inks
 * that are DISTINGUISHABLE from each other on one dark slab, which is a
 * constraint the prose scale has no reason to satisfy and does not. Promoting
 * them into the token set would put four colours in it that no piece of prose
 * may ever use.
 *
 * ## Why they are here rather than inline
 *
 * They shipped twice — once in the landing page's install panel, once in
 * `/how-it-works`'s — and a second copy of a palette is a palette that drifts.
 * The duplication is also what made the pair look like two independent
 * violations of `no-untokenized-colour.test.ts` rather than one shared
 * decision, which is why that test names this file as a declaration site and
 * nothing else.
 *
 * ## Measured
 *
 * Each against `--bd-ink` (`#0c0d10`), the slab every sample sits on, by the
 * WCAG 2.x relative-luminance formula — so the figures below are reproducible
 * rather than remembered. All four clear the 4.5:1 small-text floor
 * comfortably; `punctuation` is the tightest and is still well clear, which is
 * the one to re-measure if the slab ever lightens.
 */
export const CODE_SYNTAX = {
  /** Ordinary code — identifiers, whitespace, anything unclassified. 15.90:1 */
  plain: '#e8e8ec',
  /** Tags and punctuation — the quietest of the four. 6.80:1 */
  punctuation: '#8b93e0',
  /** String literals. 14.00:1 */
  string: '#7ef0c2',
} as const;

/** Attribute names take the brand cyan, which is already a measured token. */
export const CODE_SYNTAX_ATTR = 'var(--bd-cyan)';
